import React, { useState, useEffect, useContext } from 'react';
import { Redirect } from 'react-router-dom';
import styled from 'styled-components';

import {
	Typography,
	Avatar,
	TextField,
	InputAdornment,
	LinearProgress,
	Grow,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';

import {
	getNetzbetreiberData,
	getSpannungsebeneData,
	postLastgangRaw,
	putLastgangData,
} from '../../services/lastgangService';

import { DASHBOARD_PATH } from '../../routes';
import { setJwt } from '../../services/httpService';
import { UserContext } from '../../context/UserState';
import { lastGangHochladenLoadingTimeHardCoded } from '../../config.json';

import DropzoneDefault from '../common/DropzoneDefault';
import ModalLoadingScreen from '../common/ModalLoadingScreen';
import IconEdit from '../../assets/icon-edit.svg';
import DialogDefault from '../common/DialogDefault';

const RootForm = styled.form`
	.progress-bar {
		height: 10px;
		background-color: ${(props) => props.theme.palette.progressBar.backgroundColor};
		border-radius: 5px;

		& > div {
			background-color: ${(props) => props.theme.palette.progressBar.progressColor};
		}
	}

	.icon {
		max-width: 30px;
		width: 100%;
	}

	.add-info {
		margin-top: 20px;
		display: flex;

		& > * {
			flex: 1;
			&:not(:first-child) {
				margin-left: 20px;
			}
		}
	}

	.MuiOutlinedInput-inputAdornedStart {
		padding-left: 20px;
	}

	.Mui-disabled {
		&.MuiFormLabel-root,
		&.MuiInputBase-root,
		&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline,
		&.MuiIconButton-root {
			color: ${(props) => props.theme.palette.input.disabled.color};
			border-color: ${(props) => props.theme.palette.input.disabled.borderColor};
		}
	}
`;

const Headline = styled.div`
	display: flex;
	align-items: center;
	font-size: 20px;
	text-transform: uppercase;
	font-weight: bold;

	.MuiAvatar-root {
		margin-right: 20px;
	}
`;

const Content = styled.div`
	margin: 0 0 60px 60px;
`;

const LoadProfile = styled.div`
	.dropzone {
		display: flex;

		& > * {
			flex: 0 1 50%;
		}

		.dropzone__info-text {
			margin-left: 20px;

			h6 {
				margin-bottom: 10px;
				font-size: 14px;
				font-weight: bold;

				&:not(:first-child) {
					margin-top: 20px;
				}
			}
		}
	}
`;

const AddInformation = styled.div`
	margin-top: 20px;
`;

const Contact = styled.div`
	.user-input {
		display: flex;
		margin-top: 20px;

		& > * {
			flex: 0 1 50%;

			&:not(:first-child) {
				margin-left: 20px;
			}
		}
	}
`;

const ModalUploadLoadProfile = ({
	openLastgangDialog,
	toggleModal,
	selectedLocationID = false,
	selectedLocationName,
	comingFromPortolio,
}) => {
	const [openLoadingModal, setOpenLoadingModal] = useState(false);
	const [isUploading, setIsUploading] = useState(0); // 0 false /1 true /2 done
	const [isDisabled, setIsDisabled] = useState(true);
	const [redirectToDashboard, setRedirectToDashboard] = useState(false);
	const [fileName, setFileName] = useState('Temp. Name des Projekts');
	const [fileType, setFileType] = useState(null);
	const [uploadedFile, setUploadedFile] = useState(null);
	const [generatedLastGangID, setGeneratedLastgangID] = useState('');

	const [netzbetreiberData, setNetzbetreiberData] = useState([{}]);
	const [netzbetreiberID, setNetzbetreiberID] = useState('');

	const [spannungsebeneData, setSpannungsebeneData] = useState([{}]);
	const [spannungsebeneID, setSpannungsebeneID] = useState('');

	const [optimierungsZielData] = useState(['Eigenverbrauch', 'Netzentgelt']);
	const [optimierungsziel, setOptimierungsziel] = useState('');
	const [userName, setUserName] = useState('dummy');
	const [phoneNumber, setPhoneNumber] = useState('dummy');

	const [uploadProgressBar, setUploadProgressBar] = useState(0);
	const [loadingModalProgressBar, setLoadingModalProgressbar] = useState(0);
	const { user, setUser } = useContext(UserContext);
	const [responseLocationID, setResponseLocationID] = useState('');

	const LOADINGTIMEHARDCODED = lastGangHochladenLoadingTimeHardCoded;

	useEffect(() => {
		function progress() {
			setUploadProgressBar((oldCompleted) => {
				if (isUploading === 0) return 0;
				if (oldCompleted === 100 || isUploading === 2) {
					return 100;
				}
				const diff = Math.random() * 10;
				return Math.min(oldCompleted + diff, 100);
			});
		}
		const timer = setInterval(progress, 1000);
		return () => {
			clearInterval(timer);
		};
	}, [isUploading]);

	useEffect(() => {
		if (openLoadingModal === false) return;
		function progress() {
			setLoadingModalProgressbar((oldCompleted) => {
				const diff = ((100 / LOADINGTIMEHARDCODED) * 1000) / 2;
				return oldCompleted + diff;
			});
		}
		const timer = setInterval(progress, 500);
		return () => {
			clearInterval(timer);
		};
	}, [openLoadingModal, LOADINGTIMEHARDCODED]);

	useEffect(() => {
		const fetchData = async () => {
			getNetzbetreiberData().then((res) => {
				setNetzbetreiberData(res.data);
			});
		};

		fetchData();
	}, []);

	useEffect(() => {
		if (uploadedFile === null) {
			return;
		}

		const fetchData = async () => {
			postLastgangRaw(fileType, uploadedFile, selectedLocationID).then(
				(response) => {
					setIsUploading(2);
					setGeneratedLastgangID(Object.keys(response.data.customerData)[0]);
					setResponseLocationID(response.data.id); // ResponseLocation ID is only needed if user is not logged in and project / lastgang will be created.
					if (!user.isLoggedIn) {
						localStorage.setItem('anonymousLastgangId', response.data.user);
						setJwt(response.data.user);
					}
				}
			);
		};

		fetchData();
	}, [uploadedFile]);

	const readAndConvertFile = (file) => {
		const fileNameConverted = file[0].name.split('.')[0];
		setFileType(file[0].name.split('.').pop(-1));
		const currentLoadedFile = file[0];

		const reader = new FileReader();
		reader.readAsDataURL(currentLoadedFile);

		reader.onload = (evt) => {
			const base64File = evt.target.result.split(',')[1];
			setUploadedFile(base64File);
			setIsUploading(1);
			setFileName(fileNameConverted);
		};
	};

	const submitLastgang = async (e) => {
		e.preventDefault();
		setOpenLoadingModal(true);

		const data = {
			grid: netzbetreiberID, // ID Netzberteiebr
			level: spannungsebeneID, // ID Spannungsebene
			energy_price: 20.2357, // TODO: Hardcoded for prototype
			customer_name: userName,
			location: comingFromPortolio ? selectedLocationName : fileName,
			roofArea: { qm: 10000 }, // TODO: Hardcoded for prototype
			phone_number: phoneNumber,
			optimization_target: optimierungsziel,
		};

		await putLastgangData(responseLocationID, JSON.stringify(data));

		setTimeout(async () => {
			localStorage.setItem('generatedUserId', generatedLastGangID);
			setUser({ ...user, lastGangHochgeladenGetProjectData: true });
			setRedirectToDashboard(true);
		}, LOADINGTIMEHARDCODED);
	};

	const handleNetzbetreiberDataAndSpannungsebene = async (event, value) => {
		// eslint-disable-next-line
		value === '' ? setIsDisabled(true) : setIsDisabled(false);

		const getNetzbetreiberID = netzbetreiberData.filter(
			(o) => o['grid-name'] === value
		);

		if (getNetzbetreiberID.length === 0) {
			return;
		}

		// Databse-Call for the possible input (Spannungsebene) based on choosen Netzbetreiber
		const { data: newSpannungsebeneData } = await getSpannungsebeneData(
			getNetzbetreiberID[0]['grid-id']
		);

		setSpannungsebeneData(newSpannungsebeneData.levels);
		setNetzbetreiberID(getNetzbetreiberID[0]['grid-id']);
	};

	const handleSpannungsebeneData = (event, value) => {
		const getspannungsebeneID = [...spannungsebeneData].filter(
			(o) => o.level === value
		);

		if (getspannungsebeneID.length === 0) {
			return;
		}
		// Find the Grid ID of the choosen Netzbetreiber
		setSpannungsebeneID(getspannungsebeneID[0].id);
	};

	if (redirectToDashboard) {
		return <Redirect to={`${DASHBOARD_PATH}/${generatedLastGangID}`} />;
	}

	return (
		<>
			<ModalLoadingScreen
				open={openLoadingModal}
				headline="Wir berechnen Ihre Daten"
				subtext="Danach wird Ihr Einsparpotential im Dashboard angezeigt."
				loadingProgress={loadingModalProgressBar}
			/>
			<DialogDefault
				headline={
					<Headline>
						<Avatar>1</Avatar>
						Lastgang hochladen
					</Headline>
				}
				openModal={openLastgangDialog}
				closeModal={toggleModal}
				confirmButton={submitLastgang}
				confirmButtonText="Berechnen"
				maxWidth="md"
			>
				<RootForm autoComplete="off" onSubmit={submitLastgang}>
					<Content>
						<LoadProfile>
							<div className="dropzone">
								<DropzoneDefault
									accept=".csv,.xls,.xlsx,.xlm"
									isLoading={isUploading}
									uploadFile={readAndConvertFile}
									multiple={false}
									cancelLoading={() => {
										setIsUploading(0);
									}}
								/>
								<div className="dropzone__info-text">
									<Typography variant="h6">
										Was ist ein Lastgang?
									</Typography>
									<Typography variant="body2">
										Ihre Lastgangdaten erhalten Sie bei Ihrem
										Netzbetreiber. Der Aufbau der Daten in der Datei
										sollte ähnlich zu unserem Beispiel sein.
										Entscheidend ist ein Datenintervall von 15 Minuten
										und die Angabe der Leistungswerte in kW.
									</Typography>
									<Typography variant="h6">
										Wir beschaffen Ihren Lastgang!
									</Typography>
									<Typography variant="body2">
										Sollten Sie Hilfe bei der Beschaffung ihres
										Lastganges benötigen, können wir Ihren
										Netzbetreiber kontaktieren und Ihre Lastgangdaten
										beschaffen.
									</Typography>
								</div>
							</div>
						</LoadProfile>
					</Content>
					<AddInformation>
						<Headline>
							<Avatar>2</Avatar>
							Informationen ergänzen
						</Headline>
						<Content>
							<Typography variant="body2">
								Ergänzen Sie nach dem Upload Ihre Daten um ein möglichst
								aussgekräftiges Ergebnis zu erhalten.
							</Typography>
							{isUploading !== 0 && (
								<Grow in={isUploading !== 0}>
									<>
										<div className="add-info">
											<LinearProgress
												className="progress-bar"
												variant="determinate"
												value={uploadProgressBar}
											/>
										</div>
										<div className="add-info">
											<TextField
												value={fileName}
												variant="outlined"
												fullWidth
												type="text"
												InputProps={{
													startAdornment: (
														<InputAdornment position="end">
															<img
																src={IconEdit}
																alt="Bearbeiten"
																className="icon"
															/>
														</InputAdornment>
													),
												}}
												onChange={(e) => {
													setFileName(e.target.value);
												}}
											/>
										</div>
										<div className="add-info">
											<Autocomplete
												id="autocomplete-netzbetreiber"
												onInputChange={
													handleNetzbetreiberDataAndSpannungsebene
												}
												options={netzbetreiberData}
												getOptionLabel={(option) =>
													option['grid-name']
												}
												renderInput={(params) => (
													<TextField
														required
														{...params}
														label="Netzbetreiber"
														variant="outlined"
														fullWidth
													/>
												)}
												renderOption={(
													option,
													{ inputValue }
												) => {
													const matches = match(
														option['grid-name'],
														inputValue
													);
													const parts = parse(
														option['grid-name'],
														matches
													);
													return (
														<div>
															{parts.map((part, index) => (
																<span
																	key={index}
																	style={{
																		fontWeight: part.highlight
																			? 700
																			: 400,
																	}}
																>
																	{part.text}
																</span>
															))}
														</div>
													);
												}}
											/>
											<Autocomplete
												disabled={isDisabled}
												onInputChange={handleSpannungsebeneData}
												id="autocomplete-spannungsebene"
												options={spannungsebeneData}
												getOptionLabel={(option) => option.level}
												renderInput={(params) => (
													<TextField
														{...params}
														label="Spannungsebene"
														variant="outlined"
														fullWidth
													/>
												)}
											/>
											<Autocomplete
												disabled={isDisabled}
												onInputChange={(event, value) => {
													setOptimierungsziel(value);
												}}
												id="autocomplete-optimierungsziel"
												options={optimierungsZielData}
												getOptionLabel={(option) => option}
												renderInput={(params) => (
													<TextField
														{...params}
														label="Optimierungsziel"
														variant="outlined"
														fullWidth
													/>
												)}
											/>
										</div>
										<div className="add-info">
											<Typography variant="body2">
												* Wenn Sie einen Lastgang zu einem
												bestehenden Standort hochladen, hat die
												Netzbetreiberwahl Einfluss auf alle
												hochgeladenen Jahre des Standortes.
											</Typography>
										</div>
									</>
								</Grow>
							)}
						</Content>
					</AddInformation>
					<Contact>
						<Headline>
							<Avatar>3</Avatar>
							Kontaktaufnahme
						</Headline>
						<Content>
							<Typography variant="body2">
								Geben Sie Ihre Kontaktdaten an, falls Sie persönliche
								Beratung von unseren Experten erhalten möchten.
							</Typography>
							<div className="user-input">
								<TextField
									fullWidth
									label="Name"
									variant="outlined"
									name="userName"
									onChange={(e) => {
										setUserName(e.target.value);
									}}
								/>
								<TextField
									fullWidth
									label="Telefonnummer"
									variant="outlined"
									name="phoneNumber"
									type="phone"
									onChange={(e) => {
										setPhoneNumber(e.target.value);
									}}
								/>
							</div>
						</Content>
					</Contact>
				</RootForm>
			</DialogDefault>
		</>
	);
};

export default ModalUploadLoadProfile;
